import styles from '../ProductCard/ProductCard.module.scss';
import giftBadge from '@components/images/productcards/gift-badge.svg';
import ProductCardActions from '../components/ProductCardActions';
import ProductCardTarifInfos from '../components/ProductCardTarifInfos';
import ProductCardBenefits from '../components/ProductCardBenefits';
import ProductCardDiscountInfos from '../components/ProductCardDiscountInfos';
import ProductCardExpander from '../components/ProductCardExpander';
import ProductCardHeader from '../components/ProductCardHeader';
import ProductCardOptions from '../components/ProductCardOptions';
import ProductCardPrice from '../components/ProductCardPrice';
import ProductCardPricesSecondary from '../components/ProductCardPricesSecondary';
import ProductCardTabs from '../components/ProductCardTabs';
import useProductCard from '../hooks/useProductCard';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import {
  Card,
  CardBody,
  Content,
  Heading,
  IconText,
  Image,
} from '@wienenergiegithub/ui-next';

// Function to generate the product detail link with parameters, appending the anchor if needed
const getProductDetailLink = (
  detailPageLink = { link: '', withAnchor: false },
  productOption = {},
  isDiscountActive = false,
  selectedAktionCode = '',
) => {
  const { link, withAnchor } = detailPageLink;
  if (!link) return null;

  const url = new URL(link);
  const searchParams = new URLSearchParams();

  const { key, mixtype, bindung, capprice } = productOption;
  const optionsParams = [key, mixtype, bindung, capprice]
    .filter(Boolean)
    .join('-');

  if (optionsParams) searchParams.set('options', optionsParams);

  if (isDiscountActive && selectedAktionCode) {
    searchParams.set('aktion', selectedAktionCode);
  }
  let fullUrl = `${url.origin}${url.pathname}`;
  if (searchParams) {
    fullUrl = `${fullUrl}?${searchParams.toString()}`;
  }
  if (withAnchor && url.hash) {
    fullUrl = `${fullUrl}${url.hash}`;
  }
  return fullUrl;
};

const ProductCard = ({
  className,
  handleDropdownChange,
  isEditMode,
  isGeneralRecommender,
  options,
  product,
  productOption,
  productCardsRef,
  registerButton,
}) => {
  // TODO: desctructure all props and only pass down destructured props
  // TODO: add prop types to all subcomponents
  // TODO: add a hook that holds functions and null checks
  const {
    area,
    calculated,
    calculation_info: calculationInfo,
    content: {
      aktion_inline: aktionInline,
      details,
      list,
      vorteile,
      selected_aktion_code: selectedAktionCode,
    },
    existing_customer_url: existingCustomerUrl,
    image,
    key,
    tarifcard_info: tarifCardInfo,
    legal_text_calc: legalTextCalc,
    new_customer_url: newCustomerUrl,
    new_customer_urls: newCustomerUrls,
    tagline,
    title,
    type,
    url: linkToDetailPage,
  } = product;

  const {
    aktionInfoBoxes,
    aktionsInfo,
    isDiscountActive,
    isDiscountAvailable,
    isAkvAktion,
    isSelected,
    legalText,
    productColor,
    selectedAktion,
    setSelectedAktion,
    verbrauchspreise,
    grundpreise,
    isSpotProduct,
    cardPrice,
    priceSubline,
    priceModal,
  } = useProductCard(product);

  return (
    <>
      {(isDiscountAvailable || isDiscountActive) && (
        <Image alt="" className={styles.image} src={giftBadge} />
      )}
      <Card
        className={classNames(
          styles.container,
          styles[productColor],
          className,
        )}
      >
        {!!title && (
          <ProductCardHeader
            hasDiscount={isDiscountActive}
            image={image}
            tarifSubline={tagline}
            title={title}
            type={type}
          />
        )}
        <CardBody className={styles.body}>
          <ProductCardTabs productCardsRef={productCardsRef}>
            {/* First Child:  Overview (front of the card) */}
            <div className={classNames(styles.slide, styles.overview)}>
              {/* Price Section */}
              <ProductCardPrice
                area={area}
                calculated={calculated}
                calculationInfo={calculationInfo}
                details={details}
                isSpotProduct={isSpotProduct}
                legalTextCalc={legalTextCalc}
                priceModal={priceModal}
                priceSubline={priceSubline}
                prices={cardPrice}
                productDetailLink={getProductDetailLink(
                  // Link for SPOT products, with anchor
                  { link: linkToDetailPage, withAnchor: true },
                  productOption,
                  isDiscountActive,
                  selectedAktionCode,
                )}
              />
              {/* Options Section */}
              <div className={styles.optionsContainer}>
                <ProductCardPricesSecondary
                  calculated={calculated}
                  grundpreise={grundpreise}
                  isSpotProduct={isSpotProduct}
                  verbrauchspreise={verbrauchspreise}
                />
                {!!options && (
                  <ProductCardOptions
                    aktionInline={aktionInline}
                    calculated={calculated}
                    grundpreise={grundpreise}
                    handleDropdownChange={handleDropdownChange}
                    isAkvAktion={isAkvAktion}
                    isDiscountActive={isDiscountActive}
                    options={options}
                    product={product}
                    productOption={productOption}
                    selectedAktion={selectedAktion}
                    setSelectedAktion={setSelectedAktion}
                    verbrauchspreise={verbrauchspreise}
                  />
                )}
              </div>

              {/* Benefits Section */}
              {!!vorteile && (
                <ProductCardBenefits
                  aktionsInfo={aktionsInfo}
                  vorteile={vorteile}
                />
              )}
              {/* Discount Information */}
              {!!isDiscountActive && !!aktionInfoBoxes && (
                <ProductCardDiscountInfos aktionInfoBoxes={aktionInfoBoxes} />
              )}
              {/* General Tarif Information */}
              {!!(tarifCardInfo?.title || tarifCardInfo?.text) && (
                <ProductCardTarifInfos tarifCardInfo={tarifCardInfo} />
              )}

              {/* Legal Text Section */}
              {!!legalText && (
                <Content as="div" className={styles.legal} noSpacing>
                  {legalText}
                </Content>
              )}
            </div>
            {/* Second Child: Details (back of the card) */}
            <div className={classNames(styles.slide, styles.detailsContainer)}>
              <Heading as="h3" className={styles.details} size="h6">
                Preisdetails
              </Heading>
              <ProductCardExpander
                isDiscounted={isDiscountActive}
                items={list}
                productType={type}
              />
            </div>
          </ProductCardTabs>
          <ProductCardActions
            existingCustomerUrl={existingCustomerUrl}
            id={key}
            isDiscountActive={isDiscountActive}
            isGeneralRecommender={isGeneralRecommender}
            newCustomerUrl={newCustomerUrl}
            newCustomerUrls={newCustomerUrls}
            productDetailLink={getProductDetailLink(
              { link: linkToDetailPage, withAnchor: false },
              productOption,
              isDiscountActive,
              selectedAktionCode,
            )}
            productOption={productOption}
            registerButton={registerButton}
            selectedAktionCode={selectedAktionCode}
            title={title}
          />
        </CardBody>
      </Card>
      {!!isSelected && !!isEditMode && (
        <IconText
          className={styles.selectedIcon}
          glyph="info-circle"
          text="Aktuell ausgewählter Tarif"
          variant="inline"
        />
      )}
    </>
  );
};

ProductCard.propTypes = {
  className: PropTypes.string,
  handleDropdownChange: PropTypes.func,
  // exact shape of products depends on the product type
  // eslint-disable-next-line react/forbid-prop-types
  product: PropTypes.object.isRequired,
  productCardsRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  productOption: PropTypes.shape({
    key: PropTypes.string,
    mixtype: PropTypes.string,
    bindung: PropTypes.string,
    capprice: PropTypes.string,
  }),
  registerButton: PropTypes.node,
};

export default ProductCard;
